import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Spinner,
} from "@/components/ui/primitives";
import { api, APIError } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { useQueryClient } from "@tanstack/react-query";

type Search = { next?: string };

export const Route = createFileRoute("/login")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    next: (s.next as string | undefined) ?? undefined,
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const search = Route.useSearch();
  const { resolved } = useTheme();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const user = await api.login(username, password);
      qc.setQueryData(["me"], user);
      navigate({ to: search.next ?? "/" });
    } catch (e) {
      setErr(e instanceof APIError ? e.message : "Login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand/5 via-background to-background p-8">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <img
            src={
              resolved === "dark"
                ? "/uniphore-logo-gradient-white.png"
                : "/uniphore-logo-gradient-black.png"
            }
            alt="Uniphore"
            className="mx-auto h-11 w-auto"
          />
          <div className="mt-3 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Credit Approval
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Sign in to access the review console.
          </p>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={onSubmit}>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Username</label>
              <Input
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {err && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {err}
              </div>
            )}
            <Button
              type="submit"
              variant="brand"
              size="lg"
              className="w-full"
              disabled={busy}
            >
              {busy && <Spinner />}
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
